// frontend/src/components/tasks/TaskForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import api from '../../api/api';
import { toastError } from '../../utils/toasts';

const TaskForm = ({ task, clients, collaborators, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    clientId: '',
    assignedTo: '',
    category: 'accounting',
    priority: 'medium',
    dueDate: new Date().toISOString().split('T')[0],
    estimatedHours: 0,
  });

  const initialLoadRef = useRef(false);

  useEffect(() => {
    if (task && !initialLoadRef.current) {
      initialLoadRef.current = true;
      requestAnimationFrame(() => {
        setFormData({
          title: task.title || '',
          description: task.description || '',
          clientId: task.clientId?._id || task.clientId || '',
          assignedTo: task.assignedTo?._id || task.assignedTo || '',
          category: task.category || 'accounting',
          priority: task.priority || 'medium',
          dueDate: task.dueDate
            ? new Date(task.dueDate).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          estimatedHours: task.estimatedHours || 0,
        });
      });
    }
  }, [task]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'estimatedHours' ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        taskType: 'manual',
      };

      if (task) {
        await api.put(`/tasks/${task._id}`, payload);
      } else {
        await api.post('/tasks', payload);
      }

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Erreur sauvegarde tâche:', error);
      toastError('Erreur lors de la sauvegarde');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold">
            {task ? 'Modifier la tâche' : 'Nouvelle tâche'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium mb-1">Titre de la tâche</label>
            <input
              type="text"
              name="title"
              className="w-full border rounded px-3 py-2 text-sm"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          {/* Client + Collaborateur */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Client</label>
              <select
                name="clientId"
                className="w-full border rounded px-3 py-2 text-sm"
                value={formData.clientId}
                onChange={handleChange}
                required
              >
                <option value="">Sélectionner un client</option>
                {clients.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.companyName || c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Assigné à</label>
              <select
                name="assignedTo"
                className="w-full border rounded px-3 py-2 text-sm"
                value={formData.assignedTo}
                onChange={handleChange}
                required
              >
                <option value="">Sélectionner (admin / collaborateur)</option>
                {collaborators.map((u) => (
                  <option key={u._id || u.id} value={u._id || u.id}>
                    {u.name || u.email}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Catégorie / Priorité / Échéance */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Catégorie</label>
              <select
                name="category"
                className="w-full border rounded px-3 py-2 text-sm"
                value={formData.category}
                onChange={handleChange}
              >
                <option value="accounting">Comptabilité</option>
                <option value="fiscal">Fiscal</option>
                <option value="social">Social</option>
                <option value="legal">Juridique</option>
                <option value="other">Autre</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Priorité</label>
              <select
                name="priority"
                className="w-full border rounded px-3 py-2 text-sm"
                value={formData.priority}
                onChange={handleChange}
              >
                <option value="low">Basse</option>
                <option value="medium">Moyenne</option>
                <option value="high">Haute</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Échéance</label>
              <input
                type="date"
                name="dueDate"
                className="w-full border rounded px-3 py-2 text-sm"
                value={formData.dueDate}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Estimation + Description */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Heures estimées</label>
              <input
                type="number"
                name="estimatedHours"
                min="0"
                step="0.5"
                className="w-full border rounded px-3 py-2 text-sm"
                value={formData.estimatedHours}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              name="description"
              className="w-full border rounded px-3 py-2 text-sm"
              rows={4}
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              {task ? 'Enregistrer' : 'Créer la tâche'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

TaskForm.propTypes = {
  task: PropTypes.object,
  clients: PropTypes.array.isRequired,
  collaborators: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default TaskForm;
